/* Proposal: native RTL scrolling only. No cart, price, image or product-data mutations. */
(() => {
  'use strict';
  if (window.swCompactProductRail) return;
  const controllers = new WeakMap();
  const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)');

  // Fetch native, globally sorted cards without changing collection/admin order.
  async function loadNewest(root, track, refresh, signal) {
    if (!root.dataset.swNewestUrl || root.dataset.swNewestState === 'verified') return;
    const sectionId = 'sw-newest-rail-products';
    const requestAbort = new AbortController();
    const cancel = () => requestAbort.abort();
    signal.addEventListener('abort', cancel, { once: true });
    const timeout = setTimeout(cancel, 12000);
    root.dataset.swNewestState = 'loading';
    try {
      const url = new URL(root.dataset.swNewestUrl, window.location.origin);
      if (url.origin !== window.location.origin || url.searchParams.get('sort_by') !== 'created-descending') throw new Error('invalid-request');
      // Respect the approved homepage collection order for returning stock.
      const curatedReturns = root.dataset.swNewestCollectionId === '568112349457'
        && root.closest('[data-section-type="featured-collection"]')?.dataset.sectionId?.endsWith('__3f4debe9-3055-46ae-b6b5-15d1c9aa054b');
      const expectedSort = curatedReturns ? 'manual' : 'created-descending';
      url.searchParams.set('sort_by', expectedSort);
      url.searchParams.set('section_id', sectionId);
      url.searchParams.set('page', '1');
      const previewTheme = new URLSearchParams(window.location.search).get('preview_theme_id');
      if (previewTheme) url.searchParams.set('preview_theme_id', previewTheme);
      const response = await fetch(url.href, { credentials: 'same-origin', signal: requestAbort.signal });
      if (!response.ok) throw new Error('response-status');
      const finalUrl = new URL(response.url);
      if (finalUrl.origin !== url.origin || finalUrl.pathname !== url.pathname) throw new Error('unexpected-route');
      const html = await response.text();
      if (signal.aborted || !root.isConnected) return;
      const doc = new DOMParser().parseFromString(html, 'text/html');
      const sections = doc.querySelectorAll('#shopify-section-' + sectionId);
      if (sections.length !== 1) throw new Error('missing-section');
      const fragments = sections[0].querySelectorAll('[data-sw-newest-products]');
      if (fragments.length !== 1) throw new Error('missing-fragment');
      const fragment = fragments[0];
      if (fragment.dataset.swCollectionId !== root.dataset.swNewestCollectionId || fragment.dataset.swSort !== expectedSort || fragment.dataset.swPage !== '1') throw new Error('unexpected-context');
      const cards = Array.from(fragment.children).filter(el => el.classList.contains('product-block'));
      const metadata = fragment.querySelector('script[type="application/json"][data-sw-newest-order]');
      if (!metadata) throw new Error('missing-order');
      const order = JSON.parse(metadata.textContent);
      const total = Number(fragment.dataset.swProductsCount);
      if (!Number.isSafeInteger(total) || total < 0 || !Array.isArray(order) || cards.length !== Math.min(20, total) || order.length !== cards.length) throw new Error('unexpected-count');
      const ids = new Set();
      let previousCreated = Infinity;
      order.forEach((item, index) => {
        const created = Number(item.created);
        const id = String(item.id);
        const card = cards[index];
        if (!/^\d+$/.test(id) || ids.has(id) || !Number.isFinite(created) || created <= 0 || (expectedSort === 'created-descending' && created > previousCreated) || card.dataset.limoniappsDiscountninjaProductHandle !== item.handle || card.querySelector('[data-product-id]')?.dataset.productId !== id) throw new Error('invalid-product-order');
        if (!card.querySelector('.product-price > .sw-compact-rail__details-plus') || !card.querySelector(':scope > .sw-release-card__cta')) throw new Error('missing-native-actions');
        ids.add(id);
        previousCreated = created;
      });
      const apply = () => {
        if (signal.aborted || !root.isConnected) return;
        // Do not remove a focused product link while someone is using it.
        if (track.contains(document.activeElement)) {
          root.dataset.swNewestState = 'ready';
          return;
        }
        track.removeEventListener('focusout', afterFocus);
        track.replaceChildren(...cards);
        track.scrollLeft = 0;
        root.dataset.swNewestState = 'verified';
        root.dataset.swVerifiedSort = expectedSort;
        delete root.dataset.swNewestError;
        refresh();
        root.dispatchEvent(new CustomEvent('sw:rail:products-loaded', { bubbles: true, detail: { collectionId: root.dataset.swNewestCollectionId, sortBy: expectedSort } }));
      };
      const afterFocus = () => queueMicrotask(apply);
      track.addEventListener('focusout', afterFocus, { signal });
      apply();
    } catch (error) {
      if (!signal.aborted && root.isConnected) {
        // Keep usable original cards on network/render/validation failure.
        root.dataset.swNewestState = 'fallback';
        root.dataset.swNewestError = error.name === 'AbortError' ? 'request-aborted' : error.message;
      }
    } finally {
      clearTimeout(timeout);
      signal.removeEventListener('abort', cancel);
    }
  }

  function mount(root) {
    if (controllers.has(root)) return;
    const track = root.querySelector('[data-sw-rail-track]');
    const prev = root.querySelector('[data-sw-rail-prev]');
    const next = root.querySelector('[data-sw-rail-next]');
    const controls = root.querySelector('[data-sw-rail-controls]');
    const hint = root.querySelector('[data-sw-rail-hint]');
    const status = root.querySelector('[data-sw-rail-status]');
    if (!track || !prev || !next) return;
    let frame = 0, announceTimer;
    const abort = new AbortController();
    const opts = { signal: abort.signal };
    const cards = () => Array.from(track.children).filter(x => x.classList.contains('product-block'));
    function visibleItems() {
      const r = track.getBoundingClientRect();
      return cards().map((el, index) => ({ el, index, r: el.getBoundingClientRect() }))
        .filter(x => x.r.right > r.left + 2 && x.r.left < r.right - 2);
    }
    function update(announce = false) {
      frame = 0;
      const all = cards();
      const area = track.getBoundingClientRect();
      const first = all[0]?.getBoundingClientRect();
      const last = all[all.length - 1]?.getBoundingClientRect();
      const overflowing = track.scrollWidth > track.clientWidth + 2;
      if (controls) controls.hidden = !overflowing;
      if (hint) hint.hidden = !overflowing;
      // Geometry avoids depending on the browser's RTL scrollLeft sign convention.
      prev.disabled = !overflowing || !first || first.right <= area.right + 2;
      next.disabled = !overflowing || !last || last.left >= area.left - 2;
      if (announce && status) {
        const visible = visibleItems();
        if (visible.length) status.textContent = `מוצרים ${visible[0].index + 1} עד ${visible.at(-1).index + 1} מתוך ${all.length}`;
      }
    }
    function queue() {
      if (!frame) frame = requestAnimationFrame(() => update());
      clearTimeout(announceTimer);
      announceTimer = setTimeout(() => update(true), 180);
    }
    function move(delta) {
      const all = cards();
      const area = track.getBoundingClientRect();
      if (!all.length) return;
      const anchor = all.reduce((best, el, index) => {
        const distance = Math.abs(el.getBoundingClientRect().right - area.right);
        return distance < best.distance ? { index, distance } : best;
      }, { index: 0, distance: Infinity }).index;
      const target = all[Math.max(0, Math.min(all.length - 1, anchor + delta))];
      target.scrollIntoView({ inline: 'start', block: 'nearest', behavior: reduceMotion.matches ? 'auto' : 'smooth' });
    }
    prev.addEventListener('click', () => move(-1), opts);
    next.addEventListener('click', () => move(1), opts);
    track.addEventListener('scroll', queue, { ...opts, passive: true });
    track.addEventListener('keydown', event => {
      // Do not steal key handling from quickbuy forms, native buttons or selects.
      if (event.target !== track) return;
      if (event.key === 'ArrowLeft') { event.preventDefault(); move(1); }
      else if (event.key === 'ArrowRight') { event.preventDefault(); move(-1); }
      else if (event.key === 'Home' || event.key === 'End') {
        event.preventDefault();
        const all = cards();
        const el = event.key === 'Home' ? all[0] : all.at(-1);
        el?.scrollIntoView({ inline: 'start', block: 'nearest', behavior: 'auto' });
      }
    }, opts);
    track.addEventListener('focusin', event => {
      const item = event.target.closest('.product-block');
      if (!item) return;
      const a = track.getBoundingClientRect(), b = item.getBoundingClientRect();
      if (b.left < a.left || b.right > a.right) item.scrollIntoView({ inline: 'nearest', block: 'nearest', behavior: 'auto' });
    }, opts);
    const resize = new ResizeObserver(queue);
    resize.observe(track);
    controllers.set(root, () => { abort.abort(); resize.disconnect(); cancelAnimationFrame(frame); clearTimeout(announceTimer); controllers.delete(root); });
    update();
    loadNewest(root, track, update, abort.signal);
  }
  function scan(scope = document) {
    if (scope.matches?.('[data-sw-compact-rail]')) mount(scope);
    scope.querySelectorAll?.('[data-sw-compact-rail]').forEach(mount);
  }
  function unmount(scope) {
    if (scope.matches?.('[data-sw-compact-rail]')) controllers.get(scope)?.();
    scope.querySelectorAll?.('[data-sw-compact-rail]').forEach(x => controllers.get(x)?.());
  }
  document.addEventListener('shopify:section:load', event => scan(event.target));
  document.addEventListener('shopify:section:unload', event => unmount(event.target));
  // Shopify's recommendation loader injects products after DOMContentLoaded.
  new MutationObserver(records => records.forEach(record => {
    record.removedNodes.forEach(node => { if (node.nodeType === 1) unmount(node); });
    record.addedNodes.forEach(node => { if (node.nodeType === 1) scan(node); });
  })).observe(document.documentElement, { subtree: true, childList: true });
  window.swCompactProductRail = { mount: scan, unmount };
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => scan(), { once: true });
  else scan();
})();

